/** The challenge configurator (ADR-0033): public programs page, the landing's program desk, buy page and account offers. */
export default {
  label: "Configure your challenge",
  loading: "Loading the catalog…",
  empty: "No program for this combination yet.",
  product: {
    label: "Product",
    markets: "FX & CFD",
    futures: "Futures",
    perps: "24/7 crypto",
  },
  productNote: {
    markets: "Currencies, metals and indices",
    futures: "CME, NYMEX and COMEX",
    perps: "Open 24/7, weekends too",
  },
  instruments: "{n} instruments",
  evaluation: {
    label: "Evaluation",
    instant: "Instant",
    "1step": "1 Step",
    "2step": "2 Step",
    sprint: "Sprint",
  },
  size: {
    label: "Account size",
    recommended: "Recommended",
    cards: "Cards",
    table: "Table",
    view: "View",
  },
  type: { label: "Challenge type" },
  tag: {
    noDaily: "No daily drawdown",
    lowestTarget: "Lowest profit target",
    noConsistency: "No consistency rule",
    cheapest: "Most affordable",
    widest: "Widest drawdown",
    fullSplit: "Up to 100 % reward share",
    turbo: "Turbo available",
    window: "One {h}-hour session",
  },
  model: {
    label: "Challenge model",
    classic: "Classic",
    turbo: "Turbo",
    futClassic: "No activation fee",
    futTurbo: "Standard",
    classicNote: "Pay once, nothing when funded",
    plusFee: "+ {fee} when funded",
  },
  rules: {
    all: "All conditions",
    allHide: "Fewer conditions",
    extra: "More rules",
    title: "Account rules",
    split: "Reward share",
    target: "Profit target",
    daily: "Daily drawdown",
    max: "Max drawdown",
    reward: "Performance reward",
    rewardNote: "once, after {g} % growth when funded",
    tradeLoss: "Loss per trade",
    tradeLossNote: "funded account, open trades too",
    none: "None",
    less: "Lower {k}",
    more: "Higher {k}",
  },
  reward: {
    scaling: "Scaling",
    scalingCell: "+{step} % every {months} months",
    scalingNote: "up to {max} on the same account",
    title: "Reward information",
    cycle: "Reward cycle",
    cycles: { on_demand: "On demand", weekly: "Weekly", bi_weekly: "Bi-weekly", monthly: "Monthly" },
    consistency: "Consistency",
    bestDay: "Best day ≤ {v} %",
    evalConsistency: "evaluation: best day ≤ {v} %",
    days: "Min. trading days",
    daysNote: "days with ≥ {v} profit",
    rewardsIn: "Get rewards in",
    rewardsInDays: "{n} days",
    onDemand: "On demand",
    feeRefund: "Fee refund",
    feeRefundCell: "{pct} % with reward #{n}",
    cap: "First reward cap",
    minTrades: "Min. closed trades",
    bestTrade: "Best trade",
    bestTradeCell: "≤ {v} % of profit",
    payouts: "Rewards per account",
    once: "One",
  },
  details: {
    title: "Account details",
    time: "Time limit",
    unlimited: "Unlimited",
    days: "{n} days",
    window: "{h} h from the first trade",
    tradingDays: "Max trading days",
    leverage: "Leverage",
    instruments: "Instruments",
    drawdownType: "Drawdown type",
    dayEnds: "Trading day ends",
    contracts: "Max contracts",
    contractsCell: "{n} per instrument",
    flat: "Positions closed",
    flatCell: "daily at {time} {city}",
    news: "News trading",
    weekend: "Weekend holding",
    allowed: "Allowed",
    newsCell: "No new trades ±{n} min (funded)",
    weekendCell: "Closed Fri 16:55 New York (funded)",
    coach: "AI coach",
    included: "Included",
    activation: "Activation fee",
    activationCell: "{fee} when funded",
  },
  summary: {
    pick: "Pick your bonus",
    offNote: "already in the price",
    save: "Save {v}",
    currency: "USD",
    offer: "Limited offer",
    off: "{v} % OFF",
    buyGet: "BUY 1 GET {n}",
    resets: "{n} FREE RESET",
    start: "Start challenge",
    fullRules: "Full rules",
    payWith: "Pay in crypto: stablecoins (USDT, USDC) or major coins. The account opens as soon as the payment is confirmed.",
    oneTime: "one-time fee",
    whenFunded: "+ {fee} when funded",
    more: "What these mean",
  },
  tips: {
    buyGetShort: "+{n} accounts, they open with your rewards",
    resetShort: "A free restart if the evaluation fails",
    off: "Today's price is {v} % below the list price. It is already applied — no code to enter.",
    buyGet: "{n} more accounts on the same plan, size and options come with this one. They open one by one: the first with your first paid reward, the next with the one after.",
    reset: "If the evaluation doesn't go your way, we restart your account at its starting balance for free — same plan, nothing to buy again.",
  },
  table: {
    size: "Size",
    price: "Price",
    target: "Target",
    daily: "Daily",
    max: "Max",
    split: "Share",
    fee: "When funded",
    pick: "Choose {size}",
  },
  buy: {
    config: "Configuration",
    split: "Reward share",
    model: "Model",
    activation: "Activation fee",
    activationNote: "charged when the account turns funded",
    list: "List price",
    offers: "Comes with",
  },
  offers: {
    title: "Offers of this purchase",
    lead: "What came with the purchase and when it opens.",
    extra: "Extra account #{n}",
    locked: "Opens with paid reward #{n}",
    opened: "Opened",
    open: "Open it",
    void: "Cancelled",
    reset: "Free reset",
    resetReady: "The evaluation ended. Restart it at the starting balance on the same rules, free.",
    resetUse: "Use reset",
    resetDone: "Restarted. Opening the new account…",
    resetHint: {
      not_failed: "Available if this evaluation fails",
      not_evaluation: "Not available on a funded account",
      no_free_reset: "Already used",
      no_offer: "No reset with this account",
      already_restarted: "This account was already restarted",
    },
    activation: "Activation fee",
    activationDue: "{fee} due when the account turns funded",
    paidReset: "Reset after a breach",
    paidResetReady: "Restart this account at its starting balance, same rules and size, for {fee}.",
    paidResetUse: "Buy reset · {fee}",
    paidResetHint: { not_failed: "Available after a breach", already_restarted: "This account was already restarted" },
    haircut: "Reset penalty",
    haircutText: "This account was reset after a rule violation: every payout on it is paid {pct} % less, for its whole life.",
    config: "Configuration",
    split: "Reward share {v} %",
  },
  scaling: {
    title: "Scaling plan",
    lead: "Every {months} months: +{step} % of the account size, up to {max}. Same account, same terms.",
    period: "{months} months pass",
    periodCell: "ends {at}",
    growth: "Growth ≥ {pct} %",
    growthCell: "{v} of {need}",
    payouts: "Paid payouts",
    payoutsCell: "{n} of {need}",
    profit: "Account in profit",
    next: "Next size",
    up: "Scale up to {size}",
    max: "The account is at the maximum size.",
    history: "Scaled {from} → {to}",
  },
} as const;
